import React from 'react';
import { Container, Typography, Box, Paper, Divider, ThemeProvider as MuiThemeProvider } from '@mui/material';
import Header from './Header';
import Footer from './Footer';
import { ThemeProvider, useTheme } from '../context/ThemeContext';
import '../styles/Documentation.css';

const DocumentationContent = () => {
  const { theme, themeMode } = useTheme();

  return (
    <MuiThemeProvider theme={theme}>
      <div className={`app-container ${themeMode}-mode`}>
        <Header />
        
        <main className="main-content">
          <Container maxWidth="lg" className="documentation-container">
            <Paper elevation={2} className="documentation-paper">
              <Typography variant="h3" component="h1" className="documentation-title">
                Evidencer Record Query Documentation
              </Typography>
              
              <Divider className="section-divider" />
              
              <section className="documentation-section">
                <Typography variant="h4" component="h2" className="section-title">
                  Overview
                </Typography>
                <Typography paragraph>
                  The Evidencer Record Query application allows you to retrieve and view blockchain evidence 
                  and legal records stored as NFTs on the Ethereum blockchain. This tool provides a simple 
                  interface to access your Evidencer records using the record number (NFT token ID).
                </Typography>
                <Typography paragraph>
                  Each Evidencer record contains metadata and potentially images or other content stored on IPFS 
                  (InterPlanetary File System), a distributed storage system. This application allows you to 
                  view this information in a user-friendly format.
                </Typography>
              </section>
              
              <Divider className="section-divider" />
              
              <section className="documentation-section">
                <Typography variant="h4" component="h2" className="section-title">
                  Getting Started
                </Typography>
                <Typography paragraph>
                  To use the Evidencer Record Query application, follow these steps:
                </Typography>
                
                <Box className="steps-container">
                  <div className="step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <Typography variant="h6" component="h3">
                        Enter Your Record Number
                      </Typography>
                      <Typography paragraph>
                        In the main query section, enter your Evidencer record number in the input field. 
                        This is the unique token ID assigned to your record when it was created.
                      </Typography>
                    </div>
                  </div>
                  
                  <div className="step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <Typography variant="h6" component="h3">
                        Fetch Record Details
                      </Typography>
                      <Typography paragraph>
                        Click the "Fetch Record Details" button to retrieve your record information from the blockchain.
                        The application will connect to the Ethereum network and fetch the associated data.
                      </Typography>
                    </div>
                  </div>
                  
                  <div className="step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <Typography variant="h6" component="h3">
                        View Your Record
                      </Typography>
                      <Typography paragraph>
                        Once fetched, the application will display:
                      </Typography>
                      <ul className="feature-list">
                        <li>Evidencer Name: The name of the contract</li>
                        <li>Evidencer Symbol: The symbol used for the NFT collection</li>
                        <li>Claim Manager Address: The Ethereum address managing this claim</li>
                        <li>Evidencer Metadata Link: Direct link to the IPFS metadata</li>
                        <li>Evidencer Content: Any images and metadata associated with your record</li>
                      </ul>
                    </div>
                  </div>
                </Box>
              </section>
              
              <Divider className="section-divider" />
              
              <section className="documentation-section">
                <Typography variant="h4" component="h2" className="section-title">
                  Understanding Your Record
                </Typography>
                
                <Typography variant="h6" component="h3" className="subsection-title">
                  Record Information
                </Typography>
                <Typography paragraph>
                  The record information section displays basic details about your Evidencer record:
                </Typography>
                <ul className="feature-list">
                  <li><strong>Evidencer Name:</strong> The formal name of the NFT contract used for records</li>
                  <li><strong>Evidencer Symbol:</strong> The ticker or symbol representing the NFT collection</li>
                  <li><strong>Claim Manager Address:</strong> The Ethereum address that controls the record (typically the address that created it)</li>
                  <li><strong>Evidencer Metadata Link:</strong> A clickable link to view the raw JSON metadata stored on IPFS</li>
                </ul>
                
                <Typography variant="h6" component="h3" className="subsection-title">
                  Content Display
                </Typography>
                <Typography paragraph>
                  The content display section shows:
                </Typography>
                <ul className="feature-list">
                  <li><strong>Images:</strong> Any images associated with your record will be displayed if they are stored unencrypted on IPFS</li>
                  <li><strong>Metadata:</strong> The structured JSON data associated with your record, including properties like description, attributes, etc.</li>
                </ul>
                <Typography paragraph>
                  <strong>Note:</strong> This application displays unencrypted content. If your record contains encrypted data, you will need to use the 
                  <a href="https://encrypted-query.evidencer.io/"> Encrypted NFT Query</a> application to decrypt and view your content.
                </Typography>
              </section>
              
              <Divider className="section-divider" />
              
              <section className="documentation-section">
                <Typography variant="h4" component="h2" className="section-title">
                  Troubleshooting
                </Typography>
                
                <Typography variant="h6" component="h3" className="subsection-title">
                  Record Not Found
                </Typography>
                <Typography paragraph>
                  If you receive an error when fetching your record, verify:
                </Typography>
                <ul className="feature-list">
                  <li>You've entered the correct record number</li>
                  <li>The record exists on the Ethereum mainnet</li>
                  <li>You have a working internet connection</li>
                </ul>
                
                <Typography variant="h6" component="h3" className="subsection-title">
                  Content Not Displaying
                </Typography>
                <Typography paragraph>
                  If your record is found but content is not displayed:
                </Typography>
                <ul className="feature-list">
                  <li>The IPFS gateway may be temporarily unavailable</li>
                  <li>The content might be stored in an encrypted format (use the Encrypted NFT Query tool instead)</li>
                  <li>The IPFS hash in the metadata might be incorrect</li>
                </ul>
              </section>
              
              <Divider className="section-divider" />
              
              <section className="documentation-section">
                <Typography variant="h4" component="h2" className="section-title">
                  Security Considerations
                </Typography>
                <Typography paragraph>
                  This application interacts with blockchain data which is public by nature. Remember:
                </Typography>
                <ul className="feature-list">
                  <li>All blockchain transactions and record metadata are public</li>
                  <li>This application only reads data and cannot modify your records</li>
                  <li>No private keys are required or used by this application</li>
                  <li>The application connects to public blockchain nodes via Infura</li>
                </ul>
              </section>
              
              <Divider className="section-divider" />
              
              <section className="documentation-section">
                <Typography variant="h4" component="h2" className="section-title">
                  Related Applications
                </Typography>
                <Typography paragraph>
                  The Evidencer ecosystem includes several applications:
                </Typography>
                <ul className="feature-list">
                  <li><a href="https://upload.freeweb3.com" target="_blank" rel="noopener noreferrer">Upload to IPFS</a> - Upload files to IPFS</li>
                  <li><a href="https://upload-encrypted.freeweb3.com" target="_blank" rel="noopener noreferrer">Encrypted Upload to IPFS</a> - Encrypt and upload files to IPFS</li>
                  <li><a href="https://encrypted-query.evidencer.io/" target="_blank" rel="noopener noreferrer">Encrypted NFT Query</a> - Query and decrypt encrypted NFT content</li>
                  <li><a href="https://artifacts.freeweb3.com" target="_blank" rel="noopener noreferrer">Digital Artifacts</a> - Create and manage digital artifacts</li>
                  <li><a href="https://tokens.smartlaw.me" target="_blank" rel="noopener noreferrer">SmartLaw Tokens</a> - Manage legal tokens</li>
                  <li><a href="https://storage.freeweb3.com" target="_blank" rel="noopener noreferrer">Decentralized Storage</a> - Use decentralized storage for evidence</li>
                </ul>
              </section>
            </Paper>
          </Container>
        </main>
        
        <Footer />
      </div>
    </MuiThemeProvider>
  );
};

const Documentation = () => {
  return (
    <ThemeProvider>
      <DocumentationContent />
    </ThemeProvider>
  );
};

export default Documentation;